import { useState, useCallback, useMemo, useEffect } from 'react';
import { debounce } from 'lodash';
import { useLazyGetCompaniesByNameParameterQuery } from '@/app/api/companiesApiSlice';
import { useLazyGetLicenseesByNameParameterQuery } from '@/app/api/licenseesApiSlice';
import { Paged } from '../shared-components/Types/Paged';
import { Company } from '../shared-components/Types/Company';
import { Licensee } from '../shared-components/Types/Licensees';

export const MIN_SEARCH_TERM_LENGTH = 3;

export const useCompanySearch = (licensee?: boolean, getOnlyReferralCompanies?: boolean) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Map<string, Paged<Company | Licensee>>>(
    new Map(),
  );
  const [triggerCompany] = useLazyGetCompaniesByNameParameterQuery();
  const [triggerLicensee] = useLazyGetLicenseesByNameParameterQuery();
  const [isLoading, setIsLoading] = useState(false);

  const performSearch = useCallback(
    async (value: string) => {
      if (value.length < MIN_SEARCH_TERM_LENGTH) {
        return;
      }
      // Check cache before making API call
      if (searchResults.has(value)) {
        return;
      }
      setIsLoading(true);
      const { data } = licensee
        ? await triggerLicensee(value)
        : await triggerCompany({
            search: value,
            getOnlyReferralCompanies,
          });
      setIsLoading(false);
      if (data) {
        setSearchResults((prevResults) => new Map(prevResults).set(value, data));
      }
    },
    [triggerCompany, triggerLicensee, licensee, getOnlyReferralCompanies, searchResults],
  );

  const debouncedSearch = useMemo(() => debounce(performSearch, 1000), [performSearch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return { searchTerm, setSearchTerm, searchResults, debouncedSearch, isLoading, setSearchResults };
};
